/**
 * sticky-cta-observer.js
 * - Shows the sticky CTA when the main Add‑to‑Cart button scrolls out of view,
 *   taking the fixed header into account.
 * - Hides the sticky CTA when the page footer scrolls into view.
 * - Reads the three Stance+ settings (observe, threshold, transition duration).
 */

document.addEventListener('DOMContentLoaded', function () {

// Capture-phase intercept: prevent ATC handler firing on scroll-only links
document.addEventListener('click', function (e) {
  const scrollLink = e.target.closest('[data-scroll-only]');
  if (scrollLink) {
    e.stopImmediatePropagation();
    e.preventDefault(); // stop the anchor jump
    const target = document.querySelector(scrollLink.getAttribute('href'));
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}, true); // true = capture phase, fires before theme bubble-phase handlers


  // 1 Pull settings (fallbacks in case something is missing)
  const settings = window.themeSettings || {};
  const observe   = !!settings.sticky_scroll_observe;               // Boolean
  const threshold = parseFloat(settings.sticky_scroll_threshold) || 0; // 0‑100 %
  const duration  = parseInt(settings.sticky_transition_duration, 10) || 300; // ms

  // If the override is disabled, exit early – original theme behaviour stays.
  if (!observe) {
    document.querySelector('.sticky-cta')?.classList.remove('is-visible');
    return;
  }

  // 2 Grab the elements we need (sticky CTA + Add‑to‑Cart button)
  const stickyCTA = document.querySelector('.sticky-cta');
  if (!stickyCTA) {
    console.warn('Sticky CTA observer: .sticky-cta element not found');
    return;
  }

  const addToCartBtn = document.querySelector('button[id^="ProductSubmitButton-"]');
  if (!addToCartBtn) {
    console.warn('Sticky CTA observer: Add‑to‑Cart button not found');
    return;
  }

  // 3 Apply the transition duration (CSS custom property)
  document.documentElement.style.setProperty(
    '--sticky-transition-duration',
    `${duration}ms`
  );

  // 4 Helper – dense threshold list for smooth updates
  function buildThresholdList() {
    const list = [];
    const steps = 100; // 0‑100 %
    for (let i = 0; i <= steps; i++) {
      list.push(i / steps);
    }
    return list;
  }

  // 5 IntersectionObserver for the Add‑to‑Cart button
  //     (now aware of the fixed header height)
  // Find the header element that sits at the top of the page.
  const headerEl = document.querySelector('header') ||
                   document.querySelector('.site-header') ||
                   document.querySelector('#Header');   // fallback guesses

  const headerHeight = headerEl ? Math.round(headerEl.getBoundingClientRect().height) : 0;

  const btnObserverOptions = {
    root: null,
    rootMargin: `-${headerHeight}px 0px 0px 0px`, // shift top edge down by header height
    threshold: buildThresholdList()
  };

  const btnObserver = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      const hiddenPercent = (1 - entry.intersectionRatio) * 100;
      if (hiddenPercent > threshold) {
        stickyCTA.classList.add('is-visible');
      } else {
        stickyCTA.classList.remove('is-visible');
      }
    });
  }, btnObserverOptions);

  btnObserver.observe(addToCartBtn);

  // 6 Helper – wait for a <footer> element to exist
  function onFooterReady(callback) {
    const existing = document.querySelector('footer');
    if (existing) {
      callback(existing);
      return;
    }
    const mo = new MutationObserver(() => {
      const f = document.querySelector('footer');
      if (f) {
        mo.disconnect();
        callback(f);
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  // 7 Footer observer – hide the sticky CTA while footer is visible
  onFooterReady(footerEl => {
    const footerObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Footer entered viewport → hide the sticky CTA
          stickyCTA.classList.remove('is-visible');
          stickyCTA.classList.add('hide-on-footer');
        } else {
          // Footer left viewport → allow button observer to decide again
          stickyCTA.classList.remove('hide-on-footer');

          // Re‑evaluate the Add‑to‑Cart button visibility.
          const rect = addToCartBtn.getBoundingClientRect();
          const vpH = window.innerHeight || document.documentElement.clientHeight;
          const buttonIsOut = rect.bottom < 0 || rect.top > vpH;

          if (buttonIsOut) {
            stickyCTA.classList.add('is-visible');
          } else {
            stickyCTA.classList.remove('is-visible');
          }
        }
      });
    }, {
      root: null,
      rootMargin: '0px 0px -20px 0px',   // hide a few pixels before the footer touches the bar
      threshold: 0
    });
    footerObserver.observe(footerEl);
  });

  // 8 Edge case – page loads with button already out of view
  requestAnimationFrame(() => {
    const rect = addToCartBtn.getBoundingClientRect();
    const vpH = window.innerHeight || document.documentElement.clientHeight;
    const out = rect.bottom < 0 || rect.top > vpH;
    if (out) stickyCTA.classList.add('is-visible');
  });
});